import { useEffect, useState } from 'react'
import { Alert, Button, DatePicker, Input, Select, Space, Tag, Typography } from 'antd'
import { ReloadOutlined } from '@ant-design/icons'
import dayjs, { type Dayjs } from 'dayjs'
import {
  MAX_DEVICE_ID_LENGTH,
  MAX_OPERATOR_LENGTH,
  type ReportFilterPatch,
  type ReportFilterValues,
} from '../../hooks/useReportFilters'
import { REPORT_DATE_FORMAT, actionLabel, nowInTz } from '../../utils/formatters'

const { RangePicker } = DatePicker
const { Text } = Typography

const TEXT_DEBOUNCE_MS = 400

interface Props {
  values: ReportFilterValues
  /** from>to message from useReportFilters; blocks the query while set. */
  error?: string
  /** Report timezone; date presets and "today" are computed in it. */
  timezone?: string
  hasActiveFilters: boolean
  onChange: (patch: ReportFilterPatch) => void
  onReset: () => void
  /** Show the Action and Operator filters (lock & unlock report). */
  lockUnlock?: boolean
}

/** A calendar day as a local dayjs, so the picker shows the same YYYY-MM-DD. */
const day = (iso: string): Dayjs | null => (iso ? dayjs(iso) : null)

function presets(timezone: string | undefined): { label: string; value: [Dayjs, Dayjs] }[] {
  const today = day(nowInTz(timezone).format(REPORT_DATE_FORMAT))!
  return [
    { label: 'Today', value: [today, today] },
    { label: 'Yesterday', value: [today.subtract(1, 'day'), today.subtract(1, 'day')] },
    { label: 'Last 7 days', value: [today.subtract(6, 'day'), today] },
    { label: 'This month', value: [today.startOf('month'), today] },
  ]
}

export function ReportFilterBar({ values, error, timezone, hasActiveFilters, onChange, onReset, lockUnlock }: Props) {
  const todayIso = nowInTz(timezone).format(REPORT_DATE_FORMAT)

  const chips: { key: string; label: string; clear: ReportFilterPatch }[] = []
  if (values.fromDate || values.toDate) {
    chips.push({
      key: 'date',
      label: `Date: ${values.fromDate || '…'} → ${values.toDate || '…'}`,
      clear: { fromDate: '', toDate: '' },
    })
  }
  if (values.deviceId) chips.push({ key: 'deviceId', label: `Device ID: ${values.deviceId}`, clear: { deviceId: '' } })
  if (values.action) chips.push({ key: 'action', label: `Action: ${actionLabel(values.action)}`, clear: { action: '' } })
  if (values.operator) chips.push({ key: 'operator', label: `Operator: ${values.operator}`, clear: { operator: '' } })

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <Space wrap>
        <RangePicker
          aria-label="Date range"
          allowEmpty={[true, true]}
          value={[day(values.fromDate), day(values.toDate)]}
          presets={presets(timezone)}
          // No future days: nothing can have happened yet. "Today" is the
          // report timezone's today, matching the server's day buckets.
          disabledDate={(d) => d.format(REPORT_DATE_FORMAT) > todayIso}
          onChange={(range) =>
            onChange({
              fromDate: range?.[0]?.format(REPORT_DATE_FORMAT) ?? '',
              toDate: range?.[1]?.format(REPORT_DATE_FORMAT) ?? '',
            })
          }
          status={error ? 'error' : undefined}
          format={REPORT_DATE_FORMAT}
        />
        <DebouncedInput
          placeholder="Device ID / Serial"
          aria-label="Device ID"
          maxLength={MAX_DEVICE_ID_LENGTH}
          value={values.deviceId}
          onCommit={(deviceId) => onChange({ deviceId })}
          width={200}
        />
        {lockUnlock && (
          <>
            <Select
              aria-label="Action"
              style={{ width: 130 }}
              value={values.action}
              onChange={(action) => onChange({ action })}
              options={[
                { value: '', label: 'All actions' },
                { value: 'LOCK', label: 'Lock' },
                { value: 'UNLOCK', label: 'Unlock' },
              ]}
            />
            <DebouncedInput
              placeholder="User / Operator"
              aria-label="Operator"
              maxLength={MAX_OPERATOR_LENGTH}
              value={values.operator}
              onCommit={(operator) => onChange({ operator })}
              width={180}
            />
          </>
        )}
        <Button icon={<ReloadOutlined />} onClick={onReset} disabled={!hasActiveFilters}>
          Reset
        </Button>
      </Space>

      {error && <Alert type="error" showIcon title={error} style={{ maxWidth: 520 }} />}

      <Space wrap size={[4, 4]}>
        {chips.map((c) => (
          <Tag key={c.key} closable onClose={() => onChange(c.clear)} color="blue">
            {c.label}
          </Tag>
        ))}
        <Text type="secondary" style={{ fontSize: 12 }}>
          Dates and times in {timezone || 'UTC'}
        </Text>
      </Space>
    </div>
  )
}

/**
 * Text filter that commits after the user pauses typing (or on Enter), so
 * every keystroke doesn't rewrite the URL and refetch. Follows `value` when it
 * changes from outside (Reset, closing a chip).
 */
function DebouncedInput({
  value,
  onCommit,
  width,
  ...rest
}: {
  value: string
  onCommit: (v: string) => void
  width: number
  placeholder: string
  maxLength: number
  'aria-label': string
}) {
  const [text, setText] = useState(value)
  const [synced, setSynced] = useState(value)
  if (value !== synced) {
    setSynced(value)
    setText(value)
  }

  useEffect(() => {
    if (text.trim() === value) return
    const t = setTimeout(() => onCommit(text.trim()), TEXT_DEBOUNCE_MS)
    return () => clearTimeout(t)
  }, [text, value, onCommit])

  return (
    <Input
      {...rest}
      allowClear
      style={{ width }}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onPressEnter={() => onCommit(text.trim())}
    />
  )
}
